/**
 * Org Structure Schemas
 *
 * HTTP-контракт для `/api/v1/org-structure/*` (Task 10): чтение дерева
 * отделов, импорт из CRM, ручное назначение руководителя. Питает слой прав
 * «руководитель видит записи подчинённых по оргструктуре»
 * (`OrgStructureRepository`, `access/record-visibility.ts`).
 */
import { z } from 'zod';

// ============================================================================
// Path Parameters
// ============================================================================

export const departmentHeadParamSchema = z.object({
  orgId: z.string().uuid(),
  departmentId: z.string().uuid(),
});

// ============================================================================
// Response Schemas
// ============================================================================

export const departmentNodeSchema = z.object({
  id: z.string(),
  name: z.string(),
  parentId: z.string().nullable(),
  path: z.string(),
  headUserId: z.string().nullable(),
  headUserName: z.string().nullable(),
  memberCount: z.number().int().nonnegative(),
  /** Сколько людей и отделов охватывает руководитель этого отдела. */
  supervisedUsers: z.number().int().nonnegative(),
  supervisedDepartments: z.number().int().nonnegative(),
  /**
   * Откуда взялся руководитель: `'manual'` — назначен человеком в Aelo,
   * `'import'` — пришёл из CRM, `null` — строка старше миграции 084.
   * Ручное назначение имеет приоритет над CRM (решение владельца, 2026-07-30).
   */
  headSource: z.enum(['manual', 'import']).nullable(),
  /** Кого называет CRM сейчас — даже если ручное назначение его отклонило. */
  crmHeadUserId: z.string().nullable(),
  crmHeadUserName: z.string().nullable(),
  /**
   * Ручное назначение расходится с CRM. Считает СЕРВЕР: правило «что считать
   * расхождением» одно и живёт в одном месте. Пустой `UF_HEAD` расхождением не
   * считается — админ мог назначить руководителя именно потому, что в CRM его
   * нет, и подсветка на каждой строке была бы шумом.
   */
  headDivergesFromCrm: z.boolean(),
  /**
   * Сотрудники этого отдела, которых портал знает, а Aelo ещё нет (миграция
   * 085) — сохранены как ожидающие, но не дают доступа и не входят в
   * `memberCount`. Отдел, где ждут пятнадцать человек, не должен выглядеть
   * как пустой.
   */
  pendingMemberCount: z.number().int().nonnegative(),
  /**
   * Имена ожидающих (миграция 085, Task 5 Minor 3 финального ревью) — длина
   * массива ВСЕГДА равна `pendingMemberCount`; отсутствующее в выгрузке имя
   * — это `null`-элемент, а не пропущенная строка, иначе счётчик и список
   * расходятся в длине и выглядят как потеря данных.
   */
  pendingMemberNames: z.array(z.string().nullable()),
  /**
   * CRM называет руководителем этого отдела человека, у которого нет
   * аккаунта в Aelo ВООБЩЕ (`crmHeadUserId` пуст, `head_user_id` тоже).
   * Отличать от обычного «руководитель не назначен»: здесь ничего назначать
   * не нужно — достаточно дождаться первого входа этого человека. Считает
   * сервер, тем же правилом, что и `headDivergesFromCrm`.
   *
   * ИСКЛЮЧАЕТ `headSource === 'manual'` — симметрично `headAwaitingAccess`,
   * тем же доводом: `head_user_id` пуст может быть не потому, что ждём
   * первого входа, а потому, что админ снял назначение вручную. Резолвер
   * отсечёт такого человека `assignable`-фильтром при входе и не даст
   * доступа — обещание «ждём» было бы невыполнимым.
   */
  headAwaitingAccount: z.boolean(),
  /**
   * CRM называет руководителем человека, у которого аккаунт УЖЕ есть
   * (резолвер связал его по почте, `crmHeadUserId` заполнен), но доступ над
   * поддеревом ему ещё не выдан (`head_user_id` пуст) — резолвер по почте
   * сознательно не выдаёт его сам (миграция 085, `resolveOrgStructureLinks`).
   * Отдел с этим флагом можно закрыть одним кликом через
   * `PATCH .../departments/:id/head` с `userId: crmHeadUserId`.
   *
   * ИСКЛЮЧАЕТ `headSource === 'manual'`: `head_user_id` может быть пуст не
   * потому, что резолвер ещё не выдал доступ, а потому, что админ явно нажал
   * «— No head —» (`setHead(..., null)` пишет `head_source: 'manual'`,
   * `crm_head_user_id` при этом не трогается). Без исключения кнопка
   * предлагала бы одним кликом отменить решение, которое админ только что
   * принял, — то же место, где резолвер уже уважает ручной выбор.
   */
  headAwaitingAccess: z.boolean(),
  /**
   * Имя, которым портал называет CRM-руководителя, — из durable-колонки
   * `orgDepartments.crmHeadName` (миграция 085, раунд правок 1). Пишет
   * ИМПОРТ, вместе с `crmHeadExternalId`, и не зависит от резолвера: строка
   * ожидания, где имя раньше искалось (`org_department_pending_members`),
   * удаляется в момент связывания, и без отдельной колонки имя терялось бы
   * безвозвратно ровно к моменту, когда оно нужнее всего — сверить с именем
   * найденного по почте Aelo-аккаунта. `null`, когда CRM не назвала
   * руководителя или его имя не было известно на момент последнего импорта
   * (например, руководитель уже был сопоставлен с аккаунтом) — тогда
   * состояние показывается без имени, а не выдумывается. Для
   * `headAwaitingAccess` пустое имя означает: сверить не с чем, и вызывающая
   * сторона обязана НЕ предлагать назначение как безопасное действие.
   */
  pendingHeadName: z.string().nullable(),
  /**
   * Отдел демо-данных (#1500, `source = 'demo'`): руководитель и участники —
   * демо-сотрудники, которые никогда не войдут, поэтому «ждём входа» и
   * «требует внимания» к нему не применимы. Уходит вместе с демо.
   * Необязательное — ответ edge-api старше этого поля (откат) читается как
   * «не демо».
   */
  isDemo: z.boolean().optional(),
});

export const orgStructureResponseSchema = z.object({
  departments: z.array(departmentNodeSchema),
  syncedAt: z.string().nullable(),
  /** Есть ли подключённая CRM, откуда можно импортировать. */
  importAvailable: z.boolean(),
});

export const importOrgStructureResponseSchema = z.object({
  departments: z.number().int().nonnegative(),
  members: z.number().int().nonnegative(),
  /**
   * Сотрудники выгрузки без аккаунта, сохранённые как ожидающие связывания
   * (миграция 085) — структура полна сразу после импорта, включая тех, кто
   * ещё не заходил.
   */
  pendingMembers: z.number().int().nonnegative(),
  headsAssigned: z.number().int().nonnegative(),
  /** Сколько ручных назначений импорт сохранил, отклонив предложенное CRM. */
  manualHeadsKept: z.number().int().nonnegative(),
  /** Сотрудники CRM без пары в Aelo — показываем поимённо. */
  unmatchedUsers: z.array(z.string()),
});

// ============================================================================
// Request Schemas
// ============================================================================

export const setDepartmentHeadSchema = z.object({
  userId: z.string().min(1).nullable(),
});

// ============================================================================
// Types
// ============================================================================

export type DepartmentHeadParam = z.infer<typeof departmentHeadParamSchema>;
export type DepartmentNode = z.infer<typeof departmentNodeSchema>;
export type OrgStructureResponse = z.infer<typeof orgStructureResponseSchema>;
export type ImportOrgStructureResponse = z.infer<typeof importOrgStructureResponseSchema>;
export type SetDepartmentHeadRequest = z.infer<typeof setDepartmentHeadSchema>;
